import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { C, LEVEL_COLORS } from '../../data/constants';
import KitsuMark from '../brand/KitsuMark';

/* ── Modal shell ─────────────────────────────────────────────────
   Portals to <body> so no ancestor's stacking context or overflow
   can clip it, and adds the behaviour every modal should have:
   click-outside to close, Escape to close, and a background scroll
   lock while it's open.                                           */
export function ModalShell({ onClose, overlayClassName = '', boxClassName = '', label, children }) {
  // Keep the latest onClose in a ref so the effect can run once on mount
  // rather than tearing down and re-adding on every parent render.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  useEffect(() => {
    const onKey = e => { if (e.key === 'Escape') closeRef.current?.(); };
    document.addEventListener('keydown', onKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  return createPortal(
    <div
      className={`modal-overlay ${overlayClassName}`.trim()}
      onClick={() => closeRef.current?.()}
      role="dialog"
      aria-modal="true"
      aria-label={label}
    >
      <div className={`modal-box ${boxClassName}`.trim()} onClick={e => e.stopPropagation()}>
        {children}
      </div>
    </div>,
    document.body
  );
}

/* ── Loading screen ──────────────────────────────────────────────
   Japanese proverbs that rotate while content loads — turns dead
   waiting time into a tiny lesson.                                */
const LOADING_QUOTES = [
  { jp: '継続は力なり',         romaji: 'Keizoku wa chikara nari',      en: 'Consistency is strength.' },
  { jp: '七転び八起き',         romaji: 'Nanakorobi yaoki',             en: 'Fall seven times, stand up eight.' },
  { jp: '千里の道も一歩から',   romaji: 'Senri no michi mo ippo kara',  en: 'A journey of a thousand miles begins with a single step.' },
  { jp: '習うより慣れよ',       romaji: 'Narau yori nareyo',            en: 'Experience is the best teacher.' },
  { jp: '塵も積もれば山となる', romaji: 'Chiri mo tsumoreba yama to naru', en: 'Even dust, piled up, becomes a mountain.' },
  { jp: '初心忘るべからず',     romaji: 'Shoshin wasuru bekarazu',      en: "Never forget your beginner's spirit." },
  { jp: '石の上にも三年',       romaji: 'Ishi no ue ni mo sannen',      en: 'Patience wins in the end.' },
  { jp: '為せば成る',           romaji: 'Naseba naru',                  en: 'If you try, it will happen.' },
  { jp: '一期一会',             romaji: 'Ichigo ichie',                 en: 'Treasure every moment — it comes once.' },
  { jp: '好きこそ物の上手なれ', romaji: 'Suki koso mono no jouzu nare', en: 'You excel at what you love.' },
];

export function LoadingScreen({ label = 'Loading' }) {
  const [index, setIndex] = useState(() => Math.floor(Math.random() * LOADING_QUOTES.length));

  useEffect(() => {
    const id = setInterval(() => setIndex(i => (i + 1) % LOADING_QUOTES.length), 2800);
    return () => clearInterval(id);
  }, []);

  const quote = LOADING_QUOTES[index];

  return (
    <div className="loading-screen" role="status" aria-live="polite">
      {/* The mark itself is the spinner — the body turns while the eye
          stays upright, so it reads as waiting rather than as a generic
          throbber. */}
      <div className="loading-orb">
        <span className="loading-orb-ring" />
        <KitsuMark size={54} animation="spin" interactive={false} />
      </div>

      <div className="loading-label">{label}</div>

      <div className="loading-quote" key={index}>
        <div className="loading-quote-jp">{quote.jp}</div>
        <div className="loading-quote-romaji">{quote.romaji}</div>
        <div className="loading-quote-en">{quote.en}</div>
      </div>
    </div>
  );
}

export function Tag({ color = C.muted, children }) {
  return (
    <span style={{ background:`${color}18`, border:`1px solid ${color}35`, color, fontSize:11, fontWeight:700, borderRadius:6, padding:'3px 8px', letterSpacing:0.4, display:'inline-flex', alignItems:'center' }}>
      {children}
    </span>
  );
}

export function LevelBadge({ level }) {
  const color = LEVEL_COLORS[level] || C.muted;
  return <span className="badge" style={{ background:`${color}18`, border:`1px solid ${color}35`, color }}>{level}</span>;
}

export function StatusBadge({ status }) {
  const map = {
    active:    { color: C.green,  bg:'rgba(34,197,94,0.12)',   label:'Active'    },
    inactive:  { color: C.muted,  bg:'rgba(255,255,255,0.06)', label:'Inactive'  },
    banned:    { color: C.red,    bg:'rgba(239,68,68,0.12)',   label:'Banned'    },
    published: { color: C.green,  bg:'rgba(34,197,94,0.12)',   label:'Published' },
    draft:     { color: C.yellow, bg:'rgba(234,179,8,0.12)',   label:'Draft'     },
  };
  const s = map[status] || { color:C.muted, bg:'rgba(255,255,255,0.06)', label:status };
  return (
    <span className="badge" style={{ background:s.bg, color:s.color, border:`1px solid ${s.color}28` }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background:s.color, display:'inline-block' }} />
      {s.label}
    </span>
  );
}

export function TypeBadge({ type }) {
  const colors = { noun:C.blue, verb:C.accent, adj:C.green, adverb:C.yellow, particle:C.purple, expression:C.muted };
  const color = colors[type] || C.muted;
  return <span className="badge" style={{ background:`${color}18`, border:`1px solid ${color}28`, color }}>{type}</span>;
}

export function StatCard({ icon: Icon, label, value, sub, color = C.accent }) {
  return (
    <div className="ks-card" style={{ padding:'18px 20px', display:'flex', gap:14, alignItems:'center' }}>
      <div style={{ width:44, height:44, borderRadius:10, background:`${color}16`, color, fontSize:20, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
        <Icon />
      </div>
      <div>
        <div style={{ fontSize:22, fontWeight:900, lineHeight:1, letterSpacing:-0.5 }}>{value}</div>
        <div style={{ fontSize:12, color:C.muted, marginTop:3 }}>{label}</div>
        {sub && <div style={{ fontSize:11, color:C.green, marginTop:2, fontWeight:600 }}>{sub}</div>}
      </div>
    </div>
  );
}

export function ProgressBar({ value, color = C.accent, height = 5 }) {
  return (
    <div style={{ height, background:C.border, borderRadius:4 }}>
      <div style={{ width:`${value}%`, height:'100%', background:color, borderRadius:4, transition:'width 0.5s ease' }} />
    </div>
  );
}

export function PageHeader({ title, sub, action }) {
  return (
    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', marginBottom:24, flexWrap:'wrap', gap:12 }}>
      <div>
        <h1 style={{ fontSize:22, fontWeight:900, letterSpacing:-0.6, marginBottom:3 }}>{title}</h1>
        {sub && <p style={{ fontSize:13, color:C.muted }}>{sub}</p>}
      </div>
      {action}
    </div>
  );
}

export function ConfirmModal({ message, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-box anim-pop" style={{ maxWidth:400 }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize:34, marginBottom:12 }}>⚠️</div>
        <h2 style={{ fontSize:18, fontWeight:800, marginBottom:8 }}>Are you sure?</h2>
        <p style={{ color:C.muted, fontSize:14, marginBottom:24, lineHeight:1.65 }}>{message}</p>
        <div style={{ display:'flex', gap:10 }}>
          <button className="btn-ghost" style={{ flex:1, justifyContent:'center' }} onClick={onCancel}>Cancel</button>
          <button className="btn-danger" style={{ flex:1, justifyContent:'center', padding:'10px 20px', fontSize:14 }} onClick={onConfirm}>Delete</button>
        </div>
      </div>
    </div>
  );
}

export function FormGroup({ label, children }) {
  return (
    <div style={{ marginBottom:14 }}>
      <label className="form-label">{label}</label>
      {children}
    </div>
  );
}

export function FilterRow({ children }) {
  return <div style={{ display:'flex', gap:10, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>{children}</div>;
}

export function EmptyState({ icon: Icon, title, sub }) {
  return (
    <div style={{ padding:'56px 20px', textAlign:'center', color:C.muted }}>
      <div style={{ display:'flex', justifyContent:'center', marginBottom:14 }}>
        <KitsuMark size={54} animation="float" interactive={false} />
      </div>
      <Icon size={26} style={{ marginBottom:10, opacity:0.3 }} />
      <div style={{ fontWeight:700, fontSize:15, marginBottom:5, color:C.text }}>{title}</div>
      <div style={{ fontSize:13 }}>{sub}</div>
    </div>
  );
}

export function Toast({ message }) {
  return message ? (
    <div className="toast">✓ {message}</div>
  ) : null;
}
